/**
 * CASHDAP-3292: one-shot signal that a remember-me session was successfully minted this visit.
 * Marked by `useMintPersistentSessionOnLogin` when `newPersistentSession` fulfils with true, and
 * consumed exactly once by the summary issuance toast so it shows on the first screen after the mint.
 *
 * Module state, not redux, for the same reason as loginSignal.ts: the auth slice is persisted to
 * sessionStorage, so a redux flag would need arming/disarming cases for every path that could leave
 * it stale (abandoned checkouts, reloads, logout). Module state simply drops on reload — the toast
 * is skipped, which is acceptable for a one-time courtesy notice — and it is only ever set at the
 * single mint-success site, so there are no false positives. Never touched during SSR (set and
 * consumed in client effects).
 *
 * The signal is subscribable because the mint is best-effort and races the post-login routing: when
 * `/persistent-login/new` resolves only after LoggedIn has already routed to summary, the toast
 * hook has mounted and found no signal — the mark must notify it, or the toast is silently missed.
 */
let mintedThisVisit = false

const listeners = new Set<() => void>()

export const markRememberMeMinted = (): void => {
  mintedThisVisit = true
  listeners.forEach((listener) => listener())
}

/**
 * Returns whether a mint happened since the last consumption, and clears the signal so the toast
 * shows at most once per mint.
 */
export const consumeRememberMeMinted = (): boolean => {
  if (mintedThisVisit) {
    mintedThisVisit = false
    return true
  }
  return false
}

/**
 * Notifies `listener` whenever a mint is marked (in addition to any mark that happened before
 * subscription — callers should check `consumeRememberMeMinted()` once up front). Returns the
 * unsubscribe function.
 */
export const subscribeRememberMeMinted = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
